import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, TextField, Typography, Modal, Switch, Checkbox } from '@mui/material';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { ConstantActions, ManagementActions } from '../../../Services/_actions';
import { ProfileObject } from '../../../interfaces/Management.interface.ts';
import { useRecoilState, useRecoilValue } from 'recoil';
import { profileObjAtom } from '../../../Services/_states/Management/management.ts';
import { useNavigate, useParams } from 'react-router-dom';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import { MRT_ColumnDef } from 'material-react-table';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { globalpermissions } from '../../../Services/Permission.js';
import { convertToReadableFormat } from '../../../utils/helper.jsx';
import CommonTable from '../../Shared/Tables/TableV1.tsx';

const GeneralUserLevelPermission: React.FC = () => {
  const [profileObject, setProfileObject] = useRecoilState(profileObjAtom);
  const actions = ManagementActions();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showSnackBar } = useSnackBar();
  const { mode } = useParams();
  const [permissionList, setPermissionList] = useState < ProfileObject[] > ([]);
  const columns = useMemo < MRT_ColumnDef < ProfileObject > [] > (
    () => [
      {
        accessorKey: 'index',
        header: 'S.No.',
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => {
          const rowIndex = row.index;
          const page = 0; // Fallback to 0 if undefined
          const limit = 10; // Fallback to 10 if undefined
          return page * limit + rowIndex + 1;
        },
      },
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        size: 500,
      },
      {
        accessorKey: 'is_allowed',
        header: 'Is Allowed',
        enableSorting: false,
        size: 40,
        Cell: ({ cell }: any) => (
          <Checkbox
            checked={cell.getValue()}
            onChange={(e) =>
              handleCheckboxChange(cell.row.original, e.target.checked)
            }
          // disabled={mode === 'view' || !cell?.row?.original?.id}
          />
        ),
      },
    ],
    [profileObject?.general_user_permission]
  );
  const handleCheckboxChange = async (row: ProfileObject, isChecked: boolean) => {
    if (mode === 'view') return;
    try {
      const objToUpdate = { ...row, is_allowed: isChecked }
      if (objToUpdate?.id) {
        const res = await actions.updateGeneralUserLevelPermission(objToUpdate, objToUpdate?.id);

        if (res?.status === 200) {
          setPermissionList((prevPermissionList) =>
            prevPermissionList.map((perm) =>
              perm.id === row.id ? { ...perm, is_allowed: isChecked } : perm
            )
          );

          setProfileObject((prevProfileObject) => {
            const newPermissions = prevProfileObject?.general_user_permission?.map((profile) => {
              if (profile.id === row.id) {
                return {
                  ...profile,
                  is_allowed: isChecked,
                };
              }
              return profile;
            });

            return {
              ...prevProfileObject,
              general_user_permission: newPermissions,
            };
          });
          if (isChecked) {
            showSnackBar(`Permission for ${convertToReadableFormat(row?.name)} has been checked`, 'success');
          } else {
            showSnackBar(`Permission for ${convertToReadableFormat(row?.name)} has been unchecked`, 'success');
          }
        }
      } else if (objToUpdate?.name) {
        const objToCreate = { name: objToUpdate?.name, is_allowed: true, profile: profileObject.id }
        const res = await actions.createGeneralUserLevelPermission(objToCreate);
        if (res?.status === 200) {
          const createdPerm = res?.data?.data ?? {}
          setPermissionList((prevPermissionList) =>
            prevPermissionList.map((perm) =>
              perm.name === createdPerm?.name ? { ...createdPerm } : perm
            )
          );
          setProfileObject((prevProfileObject) => {
            return {
              ...prevProfileObject,
              general_user_permission: [...prevProfileObject?.general_user_permission, { ...createdPerm }],
            };
          });
          showSnackBar(`New permission ${convertToReadableFormat(objToCreate?.name)} has been created`, 'success');
        }
        else {
          showSnackBar(`Failed to update or created permission for ${convertToReadableFormat(objToCreate?.name)}`, 'error');
        }
      } else {
        showSnackBar(`Failed to update or created permission`, 'error');
      }
    } catch (error) {
      showSnackBar(`Failed to update permission for ${convertToReadableFormat(row?.name)}`, 'error');
    }
  };

  useEffect(() => {
    const value1 = Object.values(globalpermissions);

    const userInfo = localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo')) : {};
    const owned_permissions = userInfo?.profile?.general_user_permission || [];
    let owned_permissions_obj = {};
    for (let perm of owned_permissions) {
      owned_permissions_obj[perm.name] = perm.is_allowed;
    }

    const permissionsArray: ProfileObject[] = [];
    for (let row of value1) {
      const value2 = Object.values(row);
      for (let permission of value2) {
        if (userInfo.is_superuser === true || owned_permissions_obj[permission] === true) {
          // let is_allowed = false;
          // let id = "";
          let existingPermissionObj = { is_allowed: false, id: "" };
          if (profileObject?.general_user_permission) {
            const existingPermission = profileObject?.general_user_permission.find((perm) => perm.name === permission);
            if (existingPermission) {
              // is_allowed = existingPermission.is_allowed;
              // id = existingPermission.id;
              existingPermissionObj = { ...existingPermission };
            }
          }
          permissionsArray.push({ name: permission, ...existingPermissionObj });
        }
      }
    }
    setPermissionList(permissionsArray);
  }, []);

  return (
    <Box pb={1}>
      <Typography variant="h5" fontWeight="bold" pb={1}>
        General User Level Permission
      </Typography>
      <CommonTable
        columns={columns}
        data={permissionList || []}
        enableBottomToolbar={false}
        muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 240px)', width: '100%' } }}
      />
    </Box>
  );
};
export default GeneralUserLevelPermission;
